import React from "react";
import {
  FaFacebook,
  FaLinkedin,
  FaPinterest,
  FaWhatsapp,
  FaPhone,
  FaEnvelope,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import i18n from "../utils/i18n";
import config from "../utils/config";
import colors from "../styles/colors";
import { useTranslation } from "react-i18next";
import { PiCodeThin } from "react-icons/pi";

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  return (
    <footer
      className="w-full font-serif py-10 px-6 lg:px-12"
      style={{ color: colors.lightCream, backgroundColor: colors.darkBrown }}
    >
      {/* Horizontal Divider */}
      <div
        className="w-full max-h-[20vh] pt-8 pb-8 flex justify-center"
        style={{ backgroundColor: colors.darkBrown }}
      >
        <hr
          className="w-[23%] sm:w-[15%] md:w-[15%] lg:w-[10%] h-0.5 border-t-0 opacity-100 dark:opacity-80"
          style={{ backgroundColor: colors.lightCream }}
        />
      </div>
      {/* Felső rész - Logo, Menü, Elérhetőség */}
      <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center lg:justify-between text-center lg:text-left space-y-8 lg:space-y-0">
        {/* Logo (bal oldalt nagy képernyőn, mobilon középen) */}
        <div className="flex flex-col items-center lg:items-start w-full lg:w-1/3">
          <Link to="/">
            <img
              src="/assets/images/logo/uniart-1080-full-light.png"
              alt="Uniart logo"
              className="w-auto p-4 max-w-[180px] sm:max-w-[220px] h-auto object-contain"
            />
          </Link>
        </div>

        {/* Navigáció (középen nagy képernyőn, mobilon egy oszlopban) */}
        <nav className="w-full lg:w-1/3 flex flex-col lg:flex-row items-center justify-center space-y-4 lg:space-y-0 lg:p-4 z-40 lg:space-x-8 text-lg">
          {[
            { key: "home", label: "Home" },
            { key: "about", label: "About Us" },
            { key: "products", label: "Products" },
            { key: "portfolio", label: "Portfolio" },
            { key: "contact", label: "Contact Us" },
          ].map(({ key, label }, index) => (
            <Link
              key={index}
              to={key === "home" ? "/" : `/${key}`}
              className="transition whitespace-nowrap" // Fehér szóközök nem törhetnek új sorba
              style={{ color: colors.lightCream }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
            >
              {t(key)}
            </Link>
          ))}
        </nav>

        {/* Kapcsolati információk és nyelvek (jobb oldalon nagy képernyőn, mobilon középen) */}
        <div className="flex flex-col items-center lg:items-end w-full lg:w-1/3 space-y-3">
          <a
            href={`mailto:${config.email}`}
            className="flex items-center space-x-2 transition"
            style={{ color: colors.lightCream }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = colors.primaryGreen)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = colors.lightCream)
            }
          >
            <FaEnvelope />
            <span>{config.email}</span>
          </a>
          <a
            href={`tel:${config.phone}`}
            className="flex items-center space-x-2 transition"
            style={{ color: colors.lightCream }}
            onMouseEnter={(e) =>
              (e.currentTarget.style.color = colors.primaryGreen)
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.color = colors.lightCream)
            }
          >
            <FaPhone />
            <span>{config.phone}</span>
          </a>
          <div className="flex space-x-4">
            {["ro", "en", "hu"].map((lang) => (
              <span
                key={lang}
                onClick={() => handleLanguageChange(lang)}
                className="cursor-pointer text-lg transition"
                style={{ color: colors.lightCream }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.color = colors.primaryGreen)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.color = colors.lightCream)
                }
              >
                {lang.toLowerCase()}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Social Media ikonok */}
      <div className="flex justify-center mt-8">
        <div className="flex space-x-6">
          {[
            { icon: FaFacebook, link: config.facebookPage },
            { icon: FaWhatsapp, link: `https://wa.me/${config.phone}` },
            { icon: FaLinkedin, link: null }, // LinkedIn nincs elérhető
            { icon: FaPinterest, link: null }, // Pinterest nincs elérhető
          ].map(({ icon: Icon, link }, index) => (
            <a
              key={index}
              href={link || "#"}
              target={link ? "_blank" : undefined}
              rel={link ? "noopener noreferrer" : undefined}
              className="text-3xl transition cursor-pointer"
              style={{ color: colors.lightCream }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.color = colors.primaryGreen)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.color = colors.lightCream)
              }
              onClick={(e) => {
                if (!link) {
                  e.preventDefault(); // Megakadályozza a link követését
                  alert("Not available at the moment.");
                }
              }}
            >
              <Icon />
            </a>
          ))}
        </div>
      </div>

      {/* Elválasztó vonal */}
      <div className="w-full flex justify-center py-6">
        <hr
          className="w-1/4 sm:w-1/5 lg:w-1/5 h-0.5 border-t-0 opacity-80"
          style={{ backgroundColor: colors.lightCream }}
        />
      </div>

      {/* Copyright + Legal Notice */}
      <div className="text-center text-sm">
        UNIART © {new Date().getFullYear()}
        <span className="mx-2">|</span>
        {t("copyRight")}
        <span className="mx-2">|</span>
        <Link
          to="/legal-notice"
          className="transition duration-300"
          style={{ color: colors.lightCream }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.color = colors.primaryGreen)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = colors.lightCream)
          }
        >
          {t("legal_notice.title")}
        </Link>
      </div>

      {/* Developer Credit */}
      <div className="text-center lg:text-sm text-sm whitespace-nowrap md:text-sm mt-2 flex justify-center items-center space-x-2">
        <PiCodeThin className="lg:text-2xl text-xl md:text-2xl" style={{ color: colors.lightCream }} />
        <span>{t("dev_credit.text")}</span>
        <a
          href={config.developerLinkedIn}
          target="_blank"
          rel="noopener noreferrer"
          className="transition duration-300"
          style={{ color: colors.lightCream }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.color = colors.primaryGreen)
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.color = colors.lightCream)
          }
        >
          {t("dev_credit.name")}
        </a>
      </div>
    </footer>
  );
};

export default Footer;
